import { useEffect, useCallback, useContext } from "react";
import { useDispatch } from "react-redux";
import { setCurrentContent, setInitialContent, updateCurrentContent } from "../redux/content-slice";
import { displayPreloader } from "../redux/display-slice";
import { resetQuizContentResults, setDistributorSelectedAnswer, setQuizHasContent } from "../redux/quiz-slice";
import { updateSettings } from "../redux/settings-slice";
import { logService } from "../services/log.service";
import { useLazyGetQuizContentQuery, useLazyGetQuizQuery } from "../services/api.service";
import { useAppDispatch, useAppSelector } from "./redux.hook";
import { supplementObject } from "../utils/supplement-object";
import { QuizContent } from "../interfaces/quiz-content.interface";
import { Quiz } from "../interfaces/quiz.interface";
import { ApiError } from "../enums/api-error.enum";
import { ErrorMessageContext } from "../components/root/error-message.context";

export function useQuizData(quizId: string) {
  const [fetchQuiz] = useLazyGetQuizQuery();
  const [fetchQuizContent] = useLazyGetQuizContentQuery();
  const { distributorAnswers, customContent, customSettings } = useAppSelector((state) => state.service);
  const dispatch = useAppDispatch();
  const { setErrorMessage } = useContext(ErrorMessageContext);

  const getQuizData = useCallback(async () => {
    dispatch(displayPreloader(true));
    try {
      let mixedContent: QuizContent;
      const quiz = await fetchQuiz(quizId).unwrap();
      console.log(quiz);
      const questions = [...(quiz.content.questions || [])].sort((q1, q2) => q1.order - q2.order);
      dispatch(updateSettings({ ...quiz.settings, ...customSettings }));

      const distributorQuestion = questions.find((q) => q.distributor);
      if (distributorQuestion && distributorAnswers.length) {
        //Если distributorAnswers всего 1, то получаем id для загрузки quizContent
        if (distributorAnswers.length == 1) {
          const selectedAnswer = distributorQuestion.answers?.find((answer) => answer.id == distributorAnswers[0]);
          if (!selectedAnswer?.quizContent) throw new Error("Correct answer id is not found");

          dispatch(setDistributorSelectedAnswer(selectedAnswer));

          const quizContent = await fetchQuizContent(selectedAnswer.quizContent).unwrap();

          const questionsWithoutDistributor = questions.filter((q) => !q.distributor);
          const sortedQuizContentQuestions = [...(quizContent.content.questions || [])].sort((q1, q2) => q1.order - q2.order);
          mixedContent = supplementObject(
            {
              ...quizContent.content,
              questions: [...questionsWithoutDistributor, ...sortedQuizContentQuestions],
            },
            quiz.content
          );

          const settings = {
            ...quiz.settings,
            ...quizContent.settings,
            ...customSettings,
          };
          dispatch(updateSettings(settings));

          // Иначе фильтруем список вариантов ответов в соответствии с distributorAnswers
        } else {
          const distributorQuestionSorted = { ...distributorQuestion };
          distributorQuestionSorted.answers = distributorQuestionSorted.answers
            ?.filter((answer) => {
              return distributorAnswers.includes(String(answer.id));
            })
            .sort((itemA, itemB) => {
              const itemAIndex = distributorAnswers.findIndex((item) => item == String(itemA.id)) || -1;
              const itemBIndex = distributorAnswers.findIndex((item) => item == String(itemB.id)) || -1;
              return itemAIndex - itemBIndex;
            });
          const distributorQuestionIndex = questions.findIndex((q) => q.distributor);
          questions[distributorQuestionIndex] = distributorQuestionSorted;
          mixedContent = { ...quiz.content, questions };
        }
      } else {
        mixedContent = { ...quiz.content, questions };
      }

      if (customContent) mixedContent = supplementObject({ ...customContent }, mixedContent);
      dispatch(setCurrentContent(mixedContent));
      dispatch(setInitialContent(mixedContent));

      dispatch(setQuizHasContent());
    } catch (e) {
      console.error(e);
      logService.error(e);
      setErrorMessage({
        active: true,
        message: ApiError.GET_QUIZ_CONTENT_ERROR,
        onSubmit: () => {
          getQuizData();
          setErrorMessage({ active: false });
        },
      });
    } finally {
      dispatch(displayPreloader(false));
    }
  }, [
    quizId,
    distributorAnswers,
    customContent,
    customSettings,
    dispatch,
    fetchQuiz,
    fetchQuizContent,
    setErrorMessage,
  ]);

  //Обработка основного контента
  useEffect(() => {
    if (quizId) getQuizData();
  }, [quizId, getQuizData]);
}

export function useQuizContentData() {
  const dispatch = useDispatch();
  const [fetchQuizContent] = useLazyGetQuizContentQuery();
  const { setErrorMessage } = useContext(ErrorMessageContext);
  const { customContent, customSettings } = useAppSelector((state) => state.service);

  const getQuizContent = async (quizContentId: string) => {
    try {
      dispatch(displayPreloader(true));
      const quizContent = await fetchQuizContent(quizContentId).unwrap();

      dispatch(updateCurrentContent(supplementObject({ ...customContent }, quizContent.content)));
      dispatch(updateSettings(supplementObject({ ...customSettings }, quizContent.settings || ({} as any))));
      dispatch(resetQuizContentResults());

      return quizContent;
    } catch (e) {
      dispatch(displayPreloader(false));
      console.error(e);
      logService.error(e);

      return new Promise<Partial<Quiz>>((resolve) => {
        setErrorMessage({
          active: true,
          message: ApiError.GET_QUIZ_CONTENT_ERROR,
          onSubmit: () => {
            setErrorMessage({ active: false });
            resolve(getQuizContent(quizContentId));
          },
        });
      });
    } finally {
      setTimeout(() => {
        dispatch(displayPreloader(false));
      }, 500);
    }
  };

  return getQuizContent;
}
